// Upstream form model — ported from the legacy SwiftFederation
// upstream-hydration.ts + upstream-payload-builder.ts. The API layer
// (`useUpstreamConfig`) returns the raw upstream object; these pure helpers
// map it to/from the flat RHF form.
import type { UpstreamConfig } from '@/api/manticore';

export interface OriginHeader {
  name: string;
  value: string;
}

export interface UpstreamFormValues {
  mode: 'http' | 's3';
  // HTTP
  urls: { value: string }[]; // useFieldArray
  selector: string;
  protocol: string;
  maxRetries: string;
  connectionPoolSize: string;
  healthCheckIntervalSecs: string;
  healthCheckPath: string;
  hostHeader: string;
  sniOverride: string;
  tlsToOrigin: boolean;
  originTlsVerify: boolean;
  originCertId: string;
  originHeaders: OriginHeader[]; // useFieldArray
  // S3
  s3Service: string;
  s3Bucket: string;
  s3Region: string;
  s3AccessKeyId: string;
  s3SecretAccessKey: string;
  s3AddressingStyle: string;
  s3Endpoint: string;
  // Circuit breaker
  circuitBreakerEnabled: boolean;
  circuitBreakerThreshold: string;
  circuitBreakerRecoverySecs: string;
  circuitBreakerHalfOpen: string;
  // Origin shield
  originShield: string;
  originShieldTls: boolean;
  // Retry policy
  retryOnStatus: string;
  retryOnConnectFailure: boolean;
  retryOnTimeout: boolean;
  retryOnReset: boolean;
}

export const UPSTREAM_FORM_DEFAULTS: UpstreamFormValues = {
  mode: 'http',
  urls: [{ value: '' }],
  selector: 'round_robin',
  protocol: 'h1.1',
  maxRetries: '2',
  connectionPoolSize: '256',
  healthCheckIntervalSecs: '5',
  healthCheckPath: '/health',
  hostHeader: '',
  sniOverride: '',
  tlsToOrigin: false,
  originTlsVerify: true,
  originCertId: '',
  originHeaders: [],
  s3Service: 's3',
  s3Bucket: '',
  s3Region: 'us-east-1',
  s3AccessKeyId: '',
  s3SecretAccessKey: '',
  s3AddressingStyle: 'virtual_hosted',
  s3Endpoint: '',
  circuitBreakerEnabled: false,
  circuitBreakerThreshold: '5',
  circuitBreakerRecoverySecs: '30',
  circuitBreakerHalfOpen: '3',
  originShield: '',
  originShieldTls: false,
  retryOnStatus: '',
  retryOnConnectFailure: true,
  retryOnTimeout: true,
  retryOnReset: false,
};

export const SELECTOR_OPTIONS = [
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'weighted_round_robin', label: 'Weighted Round Robin' },
  { value: 'failover', label: 'Failover (Active/Standby)' },
  { value: 'ip_hash', label: 'IP Hash' },
  { value: 'least_connections', label: 'Least Connections' },
  { value: 'consistent_hash', label: 'Consistent Hash' },
  { value: 'random', label: 'Random' },
];

export const PROTOCOL_OPTIONS = [
  { value: 'h1.1', label: 'HTTP/1.1' },
  { value: 'h2', label: 'HTTP/2' },
];

export const S3_PROVIDER_OPTIONS = [
  { value: 's3', label: 'Amazon S3', disabled: false },
  { value: 'gcs', label: 'Google Cloud Storage', disabled: false },
  { value: 'r2', label: 'Cloudflare R2', disabled: true },
  { value: 'spaces', label: 'DigitalOcean Spaces', disabled: true },
  { value: 'b2', label: 'Backblaze B2', disabled: true },
  { value: 'wasabi', label: 'Wasabi', disabled: true },
  { value: 'minio', label: 'MinIO', disabled: true },
  { value: 'co', label: 'Custom / Other', disabled: true },
];

export const ADDRESSING_OPTIONS = [
  { value: 'virtual_hosted', label: 'Virtual Hosted Style (bucket.endpoint)' },
  { value: 'path', label: 'Path Style (endpoint/bucket/key)' },
];

type S3Defaults = Pick<UpstreamFormValues, 's3AddressingStyle' | 's3Region' | 's3Endpoint'>;

const S3_SERVICE_DEFAULTS: Record<string, Partial<S3Defaults>> = {
  s3: { s3AddressingStyle: 'virtual_hosted', s3Region: 'us-east-1', s3Endpoint: '' },
  gcs: { s3AddressingStyle: 'path', s3Region: 'auto', s3Endpoint: 'storage.googleapis.com' },
  r2: { s3AddressingStyle: 'virtual_hosted', s3Region: 'auto', s3Endpoint: '' },
  spaces: { s3AddressingStyle: 'virtual_hosted', s3Region: 'nyc3', s3Endpoint: '' },
  b2: { s3AddressingStyle: 'virtual_hosted', s3Region: 'us-west-004', s3Endpoint: '' },
  wasabi: { s3AddressingStyle: 'virtual_hosted', s3Region: 'us-east-1', s3Endpoint: '' },
  minio: { s3AddressingStyle: 'path', s3Region: 'us-east-1', s3Endpoint: '' },
  co: { s3AddressingStyle: 'virtual_hosted', s3Region: 'us-east-1', s3Endpoint: '' },
};

export function getS3ServiceDefaults(service: string, current: S3Defaults): S3Defaults {
  return { ...current, ...(S3_SERVICE_DEFAULTS[service] ?? {}) };
}

export function s3OriginUrlPreview(
  v: Pick<UpstreamFormValues, 's3Bucket' | 's3Region' | 's3Endpoint' | 's3AddressingStyle'>,
): string {
  const bucket = v.s3Bucket.trim() || '<bucket>';
  const region = v.s3Region.trim() || '<region>';
  const base = v.s3Endpoint.trim()
    ? v.s3Endpoint.trim().replace(/^https?:\/\//, '')
    : `s3.${region}.amazonaws.com`;
  return v.s3AddressingStyle === 'path'
    ? `https://${base}/${bucket}/<object-key>`
    : `https://${bucket}.${base}/<object-key>`;
}

// ── Hydration (server → form) ───────────────────────────────────────────
const num = (v: unknown, fallback: string) => (v !== undefined && v !== null ? String(v) : fallback);

export function hasUpstreamConfig(u: UpstreamConfig | null): boolean {
  const urls = (u as { urls?: string[] } | null)?.urls;
  if (!u || !urls || urls.length === 0) return false;
  return !(urls.length === 1 && !urls[0]);
}

// vEdge stores s3_service="s3" for all AWS-compatible providers — reverse-map
// to the UI dropdown using addressing_style + region + endpoint.
function inferS3Provider(style: string, region: string, endpoint: string, wireService: string): string {
  if (wireService === 'gcs' || endpoint.includes('googleapis')) return 'gcs';
  if (style === 'virtual_hosted' && region === 'auto') return 'r2';
  if (style === 'path' && region !== 'auto') return 'minio';
  if (style === 'virtual_hosted' && /^(nyc|sgp|ams|fra|sfo|lon|tor|blr)\d*/.test(region)) return 'spaces';
  if (style === 'virtual_hosted' && /^us-west-\d{3}/.test(region)) return 'b2';
  if (endpoint) return 'co';
  return 's3';
}

export function buildFormFromServer(raw: UpstreamConfig | null): UpstreamFormValues {
  if (!hasUpstreamConfig(raw)) return { ...UPSTREAM_FORM_DEFAULTS };
  const u = raw as Record<string, any>;
  const mode: 'http' | 's3' = u.s3_enabled || u.s3_auth?.enabled ? 's3' : 'http';
  const s = (u.s3_auth ?? u) as Record<string, any>;
  const cb = (u.circuit_breaker ?? {}) as Record<string, any>;

  const style = s.s3_addressing_style || s.addressing_style || 'virtual_hosted';
  const region = s.s3_region || s.region || 'us-east-1';
  const endpoint = s.s3_endpoint !== undefined ? s.s3_endpoint : s.endpoint || '';
  const wireService = s.s3_service || s.service || 's3';

  return {
    ...UPSTREAM_FORM_DEFAULTS,
    mode,
    urls: ((u.urls?.length ? u.urls : ['']) as string[]).map((value) => ({ value })),
    selector: u.selector || 'round_robin',
    protocol: u.protocol || 'h1.1',
    maxRetries: num(u.max_retries, '2'),
    connectionPoolSize: num(u.connection_pool_size, '256'),
    healthCheckIntervalSecs: num(u.health_check_interval_secs, '5'),
    healthCheckPath: u.health_check_path || '/health',
    hostHeader: u.host_header || '',
    sniOverride: u.sni_override || '',
    tlsToOrigin: u.tls_to_origin !== undefined ? Boolean(u.tls_to_origin) : false,
    originTlsVerify: u.origin_tls_verify !== undefined ? Boolean(u.origin_tls_verify) : true,
    originCertId: u.origin_cert_id || '',
    originHeaders: Array.isArray(u.origin_headers) ? u.origin_headers : [],
    s3Service: inferS3Provider(style, region, endpoint, wireService),
    s3Bucket: s.s3_bucket || s.bucket || '',
    s3Region: region,
    s3AccessKeyId: s.s3_access_key_id || s.access_key_id || '',
    // Never hydrate the secret: reads only carry a redacted value ("" or
    // "********"), and resubmitting it would overwrite the stored credential.
    s3SecretAccessKey: '',
    s3AddressingStyle: style,
    s3Endpoint: endpoint,
    circuitBreakerEnabled: Boolean(u.circuit_breaker_enabled || cb.enabled),
    circuitBreakerThreshold: String(u.circuit_breaker_threshold || cb.failure_threshold || '5'),
    circuitBreakerRecoverySecs: String(u.circuit_breaker_recovery_secs || cb.recovery_probe_interval_secs || '30'),
    circuitBreakerHalfOpen: String(u.circuit_breaker_half_open || cb.half_open_max_probes || '3'),
    originShield: u.origin_shield || '',
    originShieldTls: Boolean(u.origin_shield_tls),
    retryOnConnectFailure: u.retry_on_connect_failure !== undefined ? Boolean(u.retry_on_connect_failure) : true,
    retryOnTimeout: u.retry_on_timeout !== undefined ? Boolean(u.retry_on_timeout) : true,
    retryOnReset: Boolean(u.retry_on_reset),
    retryOnStatus: Array.isArray(u.retry_on_status) && u.retry_on_status.length ? u.retry_on_status.join(', ') : '',
  };
}

// ── Payload (form → server) ─────────────────────────────────────────────
function buildCommon(v: UpstreamFormValues): Record<string, unknown> {
  const retryOnStatusArray = v.retryOnStatus
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => !Number.isNaN(n) && n > 0);
  return {
    origin_headers: v.originHeaders.filter((h) => h.name.trim() !== ''),
    max_retries: Number(v.maxRetries),
    connection_pool_size: Number(v.connectionPoolSize),
    health_check_path: v.healthCheckPath,
    health_check_interval_secs: Number(v.healthCheckIntervalSecs),
    circuit_breaker_enabled: v.circuitBreakerEnabled,
    circuit_breaker_threshold: Number(v.circuitBreakerThreshold),
    circuit_breaker_recovery_secs: Number(v.circuitBreakerRecoverySecs),
    circuit_breaker_half_open: Number(v.circuitBreakerHalfOpen),
    circuit_breaker: {
      enabled: v.circuitBreakerEnabled,
      failure_threshold: Number(v.circuitBreakerThreshold),
      half_open_max_probes: Number(v.circuitBreakerHalfOpen),
      recovery_probe_interval_secs: Number(v.circuitBreakerRecoverySecs),
    },
    origin_shield: v.originShield,
    origin_shield_tls: v.originShieldTls,
    retry_on_status: retryOnStatusArray,
    retry_on_connect_failure: v.retryOnConnectFailure,
    retry_on_timeout: v.retryOnTimeout,
    retry_on_reset: v.retryOnReset,
  };
}

function buildS3Payload(v: UpstreamFormValues, common: Record<string, unknown>): Record<string, unknown> {
  const base = v.s3Endpoint.trim()
    ? v.s3Endpoint.trim().replace(/^https?:\/\//, '')
    : `s3.${v.s3Region}.amazonaws.com`;
  const host = v.s3AddressingStyle === 'path' ? base : `${v.s3Bucket}.${base}`;
  const wireService = v.s3Service === 'gcs' ? 'gcs' : 's3';
  return {
    ...common,
    urls: [`https://${host}`],
    weights: [],
    protocol: 'h1.1',
    selector: 'round_robin',
    tls_to_origin: true,
    origin_tls_verify: true,
    origin_cert_id: null,
    host_header: host,
    s3_enabled: true,
    s3_access_key_id: v.s3AccessKeyId,
    s3_secret_access_key: v.s3SecretAccessKey,
    s3_region: v.s3Region,
    s3_bucket: v.s3Bucket,
    s3_addressing_style: v.s3AddressingStyle,
    s3_endpoint: v.s3Endpoint,
    s3_service: wireService,
    s3_auth: {
      enabled: true,
      access_key_id: v.s3AccessKeyId,
      secret_access_key: v.s3SecretAccessKey,
      region: v.s3Region,
      bucket: v.s3Bucket,
      addressing_style: v.s3AddressingStyle,
      endpoint: v.s3Endpoint,
      service: v.s3Service,
    },
  };
}

function buildHttpPayload(v: UpstreamFormValues, common: Record<string, unknown>): Record<string, unknown> {
  const validUrls = v.urls.map((u) => u.value).filter((u) => u.trim() !== '');
  return {
    ...common,
    urls: validUrls,
    weights: validUrls.map(() => 1),
    protocol: v.protocol,
    selector: v.selector,
    host_header: v.hostHeader,
    sni_override: v.sniOverride,
    tls_to_origin: v.tlsToOrigin,
    origin_tls_verify: v.originTlsVerify,
    origin_cert_id: v.originCertId || null,
    s3_enabled: false,
  };
}

/** Returns the wire payload, or an error string for the caller to toast. */
export function buildUpstreamPayload(v: UpstreamFormValues): { payload?: Record<string, unknown>; error?: string } {
  const common = buildCommon(v);
  if (v.mode === 's3') {
    if (!v.s3Bucket.trim()) return { error: 'S3 bucket name is required' };
    // Vendors never return stored secrets (reads are redacted), so the form
    // can't round-trip one — the BE rejects S3 writes without a real secret.
    if (!v.s3SecretAccessKey.trim() || /^\*+$/.test(v.s3SecretAccessKey)) {
      return { error: 'Re-enter the S3 secret access key — stored secrets are never returned, so saving S3 settings requires it.' };
    }
    return { payload: buildS3Payload(v, common) };
  }
  const validUrls = v.urls.map((u) => u.value).filter((u) => u.trim() !== '');
  if (validUrls.length === 0) return { error: 'At least one origin URL is required' };
  return { payload: buildHttpPayload(v, common) };
}
